import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { listOpenSlots, type OpenSlot } from '../api/endpoints/availability';
import type { BusinessDetail } from '../api/schemas';
import { COUNTRY_TIMEZONE, countryOfZone, type CountryCode } from '../lib/country';
import { formatMoneyCents } from '../lib/format';
import {
  bookingLinkUrl,
  consumerOrigin,
  dayChoices,
  linkMessage,
  slotTimeLabel,
  timesFor,
  whatsAppShareUrl,
  type LinkSlot,
  type StaffChoice,
} from '../lib/reservation-link';
import { LoadingState } from './LoadingState';
import '../styles/enlace-reserva.css';

const DAYS_AHEAD = 7;
const DIAL_CODE: Readonly<Record<CountryCode, string>> = { PA: '507', VE: '58' };
const COPIED_MS = 2000;
/** Longest local number either country writes (Venezuela, without its leading 0). */
const LOCAL_MAX_DIGITS = 10;

/**
 * Copies `text` and says so for a couple of seconds. The booking link is copied from two places (the card on
 * Inicio and the finished message of the builder), so the feedback lives once.
 */
export function useCopyToClipboard(text: string): { copied: boolean; copy: () => Promise<void> } {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), COPIED_MS);
    } catch {
      setCopied(false);
    }
  }
  return { copied, copy };
}

/**
 * The builder of the reservation link: a customer writes on WhatsApp, the team picks the service and a free
 * time, and sends the link back. The customer opens it in Dale with everything chosen and only confirms with
 * their number. It lives on its own page (/enlace) and, in place, on Inicio.
 */
export function ReservationLinkBuilder({ business }: { business: BusinessDetail }) {
  const country = countryOfZone(business.neighborhood);
  const timeZone = COUNTRY_TIMEZONE[country];
  const days = useMemo(() => dayChoices(new Date(), timeZone, DAYS_AHEAD), [timeZone]);
  const origin = consumerOrigin(window.location.hostname, country);

  const [serviceId, setServiceId] = useState<string | null>(null);
  const [date, setDate] = useState(days[0]!.date);
  const [staff, setStaff] = useState<StaffChoice>('any');
  const [slot, setSlot] = useState<OpenSlot | null>(null);
  const dayRef = useRef<HTMLElement>(null);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');

  const slots = useQuery({
    queryKey: ['open-slots', business.id, serviceId, date],
    queryFn: () => listOpenSlots(business.id, serviceId!, date),
    enabled: Boolean(serviceId),
  });

  const service = business.services.find((entry) => entry.id === serviceId);
  const phone = withDialCode(customerPhone, country);

  // "Cualquiera" leaves the person open in the link; a named person rides along.
  const linkSlot: LinkSlot | undefined =
    slot && service
      ? { serviceId: service.id, startsAt: slot.startsAt, ...(staff !== 'any' && slot.staffId ? { staffId: slot.staffId } : {}) }
      : undefined;
  // The whole team, so the row stays put while the day changes.
  const people = business.staff.map((member) => ({ id: member.id, name: member.name }));
  const url = linkSlot ? bookingLinkUrl(origin, business.id, linkSlot) : undefined;
  const message = url
    ? linkMessage({ customerName, businessName: business.name, serviceName: service?.name, startsAt: slot?.startsAt, timeZone, url })
    : undefined;
  const plainMessage = linkMessage({ customerName, businessName: business.name, timeZone, url: bookingLinkUrl(origin, business.id) });

  function chooseService(id: string) {
    setServiceId(id);
    setSlot(null);
    // On a phone the times open below a long list; bring them into view.
    const smooth = window.matchMedia?.('(prefers-reduced-motion: no-preference)').matches ?? false;
    window.requestAnimationFrame(() => dayRef.current?.scrollIntoView?.({ behavior: smooth ? 'smooth' : 'auto', block: 'start' }));
  }

  function chooseStaff(next: StaffChoice) {
    setStaff(next);
    setSlot(null);
  }

  function chooseDate(next: string) {
    setDate(next);
    setSlot(null);
  }

  return (
    <>
      <section className="section" aria-labelledby="enlace-servicio">
        <h2 className="section-label" id="enlace-servicio">
          Servicio
        </h2>
        <div className="enlace-options" role="radiogroup" aria-labelledby="enlace-servicio">
          {business.services.map((entry) => (
            <button
              key={entry.id}
              type="button"
              role="radio"
              aria-checked={entry.id === serviceId}
              className="enlace-option"
              onClick={() => chooseService(entry.id)}
            >
              <span className="enlace-option-name">{entry.name}</span>
              <span className="enlace-option-sub">
                {entry.durationMin} min · {formatMoneyCents(entry.priceCents)}
              </span>
            </button>
          ))}
        </div>
      </section>

      {service ? (
        <section className="section enlace-when" aria-labelledby="enlace-dia" ref={dayRef}>
          <h2 className="section-label" id="enlace-dia">
            Día y hora
          </h2>
          <div className="enlace-days" role="radiogroup" aria-labelledby="enlace-dia">
            {days.map((day) => (
              <button
                key={day.date}
                type="button"
                role="radio"
                aria-checked={day.date === date}
                className="chip"
                data-status={day.date === date ? 'active' : undefined}
                onClick={() => chooseDate(day.date)}
              >
                {day.label}
              </button>
            ))}
          </div>
          {people.length > 1 ? (
            <div className="enlace-days" role="radiogroup" aria-label="Con quién">
              {[{ id: 'any', name: 'Cualquiera' }, ...people].map((person) => (
                <button
                  key={person.id}
                  type="button"
                  role="radio"
                  aria-checked={person.id === staff}
                  className="chip"
                  data-status={person.id === staff ? 'active' : undefined}
                  onClick={() => chooseStaff(person.id)}
                >
                  {person.name}
                </button>
              ))}
            </div>
          ) : null}
          <SlotPicker
            state={{ ...slots, data: slots.data ? timesFor(slots.data, staff) : undefined }}
            selected={slot}
            onSelect={setSlot}
            label={(entry) => slotTimeLabel(entry.startsAt, timeZone)}
          />
        </section>
      ) : null}

      <section className="section" aria-labelledby="enlace-cliente">
        <h2 className="section-label" id="enlace-cliente">
          Cliente
        </h2>
        <div className="enlace-fields">
          <label className="field">
            <span className="field-label">Nombre del cliente (opcional)</span>
            <input type="text" autoComplete="off" value={customerName} onChange={(event) => setCustomerName(event.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">WhatsApp del cliente (opcional)</span>
            <input
              type="tel"
              inputMode="tel"
              autoComplete="off"
              placeholder={country === 'VE' ? '0412 1234567' : '6123-4567'}
              value={customerPhone}
              onChange={(event) => setCustomerPhone(event.target.value)}
            />
          </label>
        </div>
      </section>

      {message && url ? <ReadyLink message={message} url={url} phone={phone} /> : null}

      <p className="enlace-plain">
        ¿Prefieres que elija la hora?{' '}
        <a href={whatsAppShareUrl(phone, plainMessage)} target="_blank" rel="noreferrer">
          Enviar el enlace de tu negocio
        </a>
      </p>
    </>
  );
}

type SlotState = { isPending: boolean; isError: boolean; data?: OpenSlot[]; refetch: () => unknown };

function SlotPicker({ state, selected, onSelect, label }: { state: SlotState; selected: OpenSlot | null; onSelect: (slot: OpenSlot) => void; label: (slot: OpenSlot) => string }) {
  if (state.isPending) return <LoadingState label="Buscando horas libres…" />;
  if (state.isError) {
    return (
      <p className="enlace-empty" role="alert">
        No pudimos traer las horas.{' '}
        <button type="button" className="linklike" onClick={() => void state.refetch()}>
          Reintentar
        </button>
      </p>
    );
  }
  const open = state.data ?? [];
  if (open.length === 0) return <p className="enlace-empty">No quedan horas libres ese día. Prueba otro.</p>;
  return (
    <div className="enlace-times" role="radiogroup" aria-label="Hora">
      {open.map((entry) => (
        <button
          key={entry.id}
          type="button"
          role="radio"
          aria-checked={entry.id === selected?.id}
          className="enlace-time"
          onClick={() => onSelect(entry)}
        >
          {label(entry)}
        </button>
      ))}
    </div>
  );
}

function ReadyLink({ message, url, phone }: { message: string; url: string; phone: string }) {
  const { copied, copy } = useCopyToClipboard(url);

  return (
    <section className="section enlace-ready" aria-labelledby="enlace-mensaje">
      <h2 className="section-label" id="enlace-mensaje">
        Mensaje
      </h2>
      <p className="enlace-message">{message}</p>
      <div className="enlace-actions">
        <a className="btn" href={whatsAppShareUrl(phone, message)} target="_blank" rel="noreferrer">
          Enviar por WhatsApp
        </a>
        <button type="button" className="btn secondary" onClick={() => void copy()} aria-live="polite">
          {copied ? 'Copiado' : 'Copiar enlace'}
        </button>
      </div>
    </section>
  );
}

/** "6123-4567" becomes 50761234567 for wa.me; an empty field stays empty. */
function withDialCode(raw: string, country: CountryCode): string {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  const dial = DIAL_CODE[country];
  // Already international: "+507 6123-4567", or "507 6123 4567" written without the plus.
  if (raw.trim().startsWith('+') || (digits.length >= LOCAL_MAX_DIGITS + dial.length && digits.startsWith(dial))) return digits;
  return `${dial}${digits.replace(/^0+/, '')}`;
}
